import React, { useEffect, useState } from "react";
import { PlayCircle, Video } from "lucide-react";
import { supabase } from "../lib/supabaseClient";

const PREFERENCE_FUNCTION_NAME = "sales_affiliate_activation_guide_preference";
const YOUTUBE_EMBED_URL =
  "https://www.youtube-nocookie.com/embed/f99JR263oMc?rel=0";

export default function SalesAffiliateActivationGuide({
  lifecycleState,
  activationGuide,
  refreshWorkspace,
}) {
  const autoShowDisabled = Boolean(activationGuide?.auto_show_disabled);
  const isActive = lifecycleState === "ACTIVE";
  const [expanded, setExpanded] = useState(
    !isActive && !autoShowDisabled
  );
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (isActive || autoShowDisabled) {
      setExpanded(false);
      return;
    }

    setExpanded(true);
  }, [autoShowDisabled, isActive]);

  const handlePreferenceChange = async (event) => {
    const checked = event.target.checked;

    if (saving) return;

    setSaving(true);
    setErrorMessage("");

    try {
      const { data, error } = await supabase.functions.invoke(
        PREFERENCE_FUNCTION_NAME,
        {
          body: {
            auto_show_disabled: checked,
          },
        }
      );

      if (error) {
        throw new Error(error.message || "Unable to save guide preference.");
      }

      if (!data?.ok) {
        throw new Error(data?.error || "Unable to save guide preference.");
      }

      if (checked) {
        setExpanded(false);
      } else if (!isActive) {
        setExpanded(true);
      }

      await refreshWorkspace?.({ background: true });
    } catch (error) {
      setErrorMessage(
        error?.message || "Unable to save guide preference. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  if (!expanded) {
    return (
      <section className="mt-8 rounded-3xl border border-blue-200 bg-blue-50/60 px-6 py-5 shadow-sm">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <Video className="mt-0.5 flex-none text-blue-600" size={24} />
            <div>
              <h2 className="text-lg font-black text-slate-950">
                Account Activation Guide
              </h2>
              <p className="mt-1 text-sm leading-6 text-slate-600">
                Rewatch the short guide to the steps required to activate your
                Sales Affiliate account.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 text-sm font-black text-white shadow-sm hover:bg-blue-700"
          >
            <PlayCircle size={18} />
            Watch Account Activation Guide
          </button>
        </div>

        {errorMessage ? (
          <div
            role="alert"
            className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700"
          >
            {errorMessage}
          </div>
        ) : null}
      </section>
    );
  }

  return (
    <section className="mt-8 overflow-hidden rounded-3xl border border-blue-200 bg-white shadow-sm">
      <div className="border-b border-blue-100 bg-blue-50 px-6 py-5">
        <div className="flex items-start gap-3">
          <Video className="mt-0.5 flex-none text-blue-600" size={26} />
          <div>
            <div className="text-xs font-black uppercase tracking-[0.22em] text-blue-600">
              Start here
            </div>
            <h2 className="mt-1 text-2xl font-black tracking-tight text-slate-950">
              Account Activation Guide
            </h2>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
              Watch this short guide before you begin. It explains the steps
              required to activate your Sales Affiliate account.
            </p>
          </div>
        </div>
      </div>

      <div className="px-6 py-6">
        <div
          className="mx-auto w-full overflow-hidden rounded-2xl bg-slate-950 shadow-sm"
          style={{ maxWidth: "820px" }}
        >
          <iframe
            src={YOUTUBE_EMBED_URL}
            title="AI Fine Protector Account Activation Guide"
            className="block aspect-video w-full bg-black"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        </div>

        <div className="mt-5 flex flex-col gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:items-center sm:justify-between">
          {!isActive ? (
            <label className="flex cursor-pointer items-start gap-3 text-sm font-semibold text-slate-700">
              <input
                type="checkbox"
                checked={autoShowDisabled}
                onChange={handlePreferenceChange}
                disabled={saving}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600"
              />
              <span>
                {saving
                  ? "Saving preference..."
                  : "Don't automatically show this guide again"}
              </span>
            </label>
          ) : (
            <div className="text-sm font-semibold text-slate-500">
              Your account is active. This guide is available anytime for reference.
            </div>
          )}

          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="text-sm font-black text-slate-600 hover:text-slate-950"
          >
            Hide guide
          </button>
        </div>

        {errorMessage ? (
          <div
            role="alert"
            className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700"
          >
            {errorMessage}
          </div>
        ) : null}
      </div>
    </section>
  );
}
